import { NotifyFormat } from '.';
import {
  DisabledNotificationChannelType,
  DisabledNotificationType,
} from '../dealerTypes';
import {
  FieldDefaultsType,
  FieldId,
  TagId,
  TagsType,
  TemplateDefinitions,
} from '../outreachTypes';

/* The list of configured template definitions, keyed by template ID, used by
 * backend for template definition, and used by Chairman to render the Outreach
 * tab UI.  The fieldIds are ordered according to how they will be drawn in
 * Chairman, and can vary by template. */
export const ConfiguredTemplateDefinitions: TemplateDefinitions = {
  [NotifyFormat.ONLINE_CONFIRMATION]: {
    name: 'Confirmation Email',
    dealerDisabledProperty: {
      channel: DisabledNotificationChannelType.emails,
      property: DisabledNotificationType.newCarIsWaiting,
    },
    fieldIds: [
      'emailSubject',
      'title',
      'greeting',
      'pickupWindow',
      'body',
      'column1Title',
      'column1Body',
      'column2Title',
      'column2Body',
      'column3Title',
      'column3Body',
      'locationAddress',
    ],
  },
  [NotifyFormat.ONLINE_TEST_DRIVE]: {
    name: 'Test Drive Email',
    dealerDisabledProperty: {
      channel: DisabledNotificationChannelType.emails,
      property: DisabledNotificationType.testDrive,
    },
    fieldIds: [
      'emailSubject',
      'title',
      'greeting',
      'dealLinkText',
      'body',
      'column1Title',
      'column1Body',
      'column2Title',
      'column2Body',
      'column3Title',
      'column3Body',
      'locationAddress',
    ],
  },
};

/* FieldId values mapped to field names, used by Chairman to render the
 * Outreach tab UI. */
export const FieldNames = {
  [FieldId.body]: 'Body',
  [FieldId.column1Body]: 'Column 1 Body',
  [FieldId.column1Title]: 'Column 1 Title',
  [FieldId.column2Body]: 'Column 2 Body',
  [FieldId.column2Title]: 'Column 2 Title',
  [FieldId.column3Body]: 'Column 3 Body',
  [FieldId.column3Title]: 'Column 3 Title',
  [FieldId.dealLinkText]: 'Deal Link Text',
  [FieldId.emailSubject]: 'Email Subject Line',
  [FieldId.greeting]: 'Greeting',
  [FieldId.locationAddress]: 'Location Address',
  [FieldId.pickupWindow]: 'Pick Up Window',
  [FieldId.title]: 'Title',
};

/* Default values for fields, used by backend and Chairman to provide values
 * when no overrides have been specified by dealers. */
export const FieldDefaults: FieldDefaultsType = {
  [NotifyFormat.ONLINE_CONFIRMATION]: {
    [FieldId.emailSubject]: '[Car] at [Dealer Name]',
    [FieldId.title]: 'Your new car is waiting!',
    [FieldId.greeting]: 'Thank you for visiting [Dealer Name]!',
    [FieldId.pickupWindow]:
      'Your scheduled [Method] time is on&nbsp;[Date and Time]',
    [FieldId.body]:
      'A member of the [Dealer Name] team will be in touch soon to answer any questions you may have. In the meantime, let&#39;s gather up the essentials you&#39;ll need to pick up your new car.',
    [FieldId.dealLinkText]: 'View details here',
    [FieldId.column1Title]: 'Valid Drivers License',
    [FieldId.column1Body]:
      'Whether you&#39;re driving off the lot or we&#39;re dropping it off at your house, a valid ID is key.',
    [FieldId.column2Title]: 'Proof of Insurance',
    [FieldId.column2Body]:
      'If <strong>you&#39;re</strong> covered, then we&#39;re covered.&nbsp;We cannot accept uninsured drivers.&nbsp;',
    [FieldId.column3Title]: 'Check or Banking Info',
    [FieldId.column3Body]:
      'Bring a check or banking info (including routing number) for your down payment.',
    [FieldId.locationAddress]: '[Dealer Address]',
  },
  [NotifyFormat.ONLINE_TEST_DRIVE]: {
    [FieldId.emailSubject]: '[Car] at [Dealer Name]',
    [FieldId.title]: 'Your test drive is waiting!',
    [FieldId.greeting]: 'Thank you for visiting [Dealer Name]!',
    [FieldId.dealLinkText]: 'Click here to finish scheduling your test drive.',
    [FieldId.body]:
      'We just have a few quick things to finish before we can get you all set up. It will take only a moment.',
    [FieldId.column1Title]: 'Valid Drivers License',
    [FieldId.column1Body]:
      'Whether you&#39;re driving off the lot or we&#39;re dropping it off at your house, a valid ID is key.',
    [FieldId.column2Title]: 'Proof of Insurance',
    [FieldId.column2Body]:
      'If <strong>you&#39;re</strong> covered, then we&#39;re covered.&nbsp;We cannot accept uninsured drivers.&nbsp;',
    [FieldId.column3Title]: 'Check or Banking Info',
    [FieldId.column3Body]:
      'Bring a check or banking info (including routing number) for your down payment.',
    [FieldId.locationAddress]: '[Dealer Address]',
  },
};

export const TagNamesByTagId: Record<TagId, string> = {
  [TagId.CAR]: 'Car',
  [TagId.DATE_AND_TIME]: 'Date and Time',
  [TagId.DATE]: 'Date',
  [TagId.DEALER_ADDRESS]: 'Dealer Address',
  [TagId.DEALER_NAME]: 'Dealer Name',
  [TagId.METHOD]: 'Method',
  [TagId.TIME]: 'Time',
};

/* A declaration of valid tags per communication channel and template.  Used by
 * Chairman to perform validation of field values that contain tags.  Not
 * presently used by backend, as backend has its own data structure that maps
 * tags to tag formatters.  The text descriptions here are not presently used,
 * but are available to Chairman for when the Outreach tab UI is next augmented
 * to provide user assistance regarding tags. */
export const Tags: TagsType = {
  default: {
    [TagId.DEALER_ADDRESS]: 'Dealership address',
    [TagId.DEALER_NAME]: 'Dealership name',
  },
  [NotifyFormat.ONLINE_CONFIRMATION]: {
    [TagId.CAR]: 'Car year, manufacturer, make, and model',
    [TagId.DATE]: 'Date of delivery or pick-up',
    [TagId.DATE_AND_TIME]: 'Date and time of delivery or pick-up',
    [TagId.METHOD]: 'Delivery or pick-up',
    [TagId.TIME]: 'Time of delivery or pick-up',
  },
  [NotifyFormat.ONLINE_TEST_DRIVE]: {
    [TagId.CAR]: 'Car name',
  },
};
